import React, { useState } from "react";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import { Overline, Container, btn } from "@/components/ui-kit";
import { Field, TextInput, TextArea, NativeSelect } from "@/components/forms";
import AntiSpam from "@/components/AntiSpam";
import { api } from "@/lib/api";
import { COMPANY } from "@/lib/content";
import { toast } from "sonner";
import { CheckCircle, ArrowRight, Phone, EnvelopeSimple, MapPin } from "@phosphor-icons/react";

const reasons = [
  "Property Submission",
  "Broker Opportunity",
  "Investment Opportunity",
  "Development Partnership",
  "General Inquiry",
];

const emailOk = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

export default function Contact() {
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [errors, setErrors] = useState({});
  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", reason: "", message: "" });
  const [spam, setSpam] = useState({});
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    const err = {};
    if (!form.name.trim()) err.name = "Required.";
    if (!emailOk(form.email)) err.email = "Valid email required.";
    if (!form.reason) err.reason = "Please select a reason.";
    if (!form.message.trim()) err.message = "Please enter a message.";
    setErrors(err);
    if (Object.keys(err).length) return;
    if (!spam.captcha_answer || spam.captcha_answer !== spam.captcha_a + spam.captcha_b) {
      toast.error("Please answer the verification question correctly.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/submissions/contact", { ...form, ...spam });
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <Seo
        title="Contact — Redwood Capital Holdings, Charlotte NC"
        description="Contact Redwood Capital Holdings, Inc. in Charlotte, North Carolina about property submissions, broker opportunities, investment, and development partnerships."
        path="/contact"
      />

      <section className="bg-forest text-white pt-20 pb-16 relative overflow-hidden">
        <div className="grain" />
        <Container className="relative">
          <Overline>Contact</Overline>
          <h1 className="mt-4 font-serif text-4xl sm:text-5xl font-semibold tracking-tight">Contact Redwood Capital</h1>
          <p className="mt-5 text-white/70 text-lg max-w-2xl leading-relaxed">
            We'd like to hear from you — whether you own land, represent a deal, or want to explore a partnership.
          </p>
        </Container>
      </section>

      <section className="py-20 md:py-24">
        <Container>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-14">
            <Reveal className="lg:col-span-4">
              <h2 className="font-serif text-2xl font-semibold text-forest">{COMPANY.name}</h2>
              <p className="text-ink mt-1">{COMPANY.location}</p>
              <ul className="mt-8 space-y-5">
                <li className="flex items-start gap-4">
                  <span className="w-11 h-11 rounded-sm bg-forest flex items-center justify-center shrink-0"><MapPin size={20} className="text-gold" /></span>
                  <div><p className="text-xs uppercase tracking-wider text-ink/60 font-semibold">Location</p><p className="text-forest font-medium">{COMPANY.location}</p></div>
                </li>
                <li className="flex items-start gap-4">
                  <span className="w-11 h-11 rounded-sm bg-forest flex items-center justify-center shrink-0"><Phone size={20} className="text-gold" /></span>
                  <div><p className="text-xs uppercase tracking-wider text-ink/60 font-semibold">Phone</p><a href={COMPANY.phoneHref} className="text-forest font-medium hover:text-gold transition-colors" data-testid="contact-phone">{COMPANY.phone}</a></div>
                </li>
                <li className="flex items-start gap-4">
                  <span className="w-11 h-11 rounded-sm bg-forest flex items-center justify-center shrink-0"><EnvelopeSimple size={20} className="text-gold" /></span>
                  <div><p className="text-xs uppercase tracking-wider text-ink/60 font-semibold">Email</p><a href={`mailto:${COMPANY.email}`} className="text-forest font-medium hover:text-gold transition-colors" data-testid="contact-email">{COMPANY.email}</a></div>
                </li>
              </ul>
            </Reveal>

            <Reveal delay={0.15} className="lg:col-span-8">
              <div className="bg-white border border-line rounded-sm p-6 sm:p-10 shadow-[0_4px_24px_rgba(13,46,27,0.04)]">
                {done ? (
                  <div className="text-center py-10" data-testid="contact-success">
                    <div className="mx-auto w-16 h-16 rounded-full bg-gold/15 flex items-center justify-center">
                      <CheckCircle size={40} weight="light" className="text-gold" />
                    </div>
                    <h3 className="mt-6 font-serif text-2xl font-semibold text-forest">Message received</h3>
                    <p className="mt-3 text-ink">Thank you for reaching out. Our team will be in touch shortly.</p>
                  </div>
                ) : (
                  <form onSubmit={submit} className="space-y-6" data-testid="contact-form">
                    <div className="grid sm:grid-cols-2 gap-6">
                      <Field label="Name" required error={errors.name}>
                        <TextInput value={form.name} onChange={set("name")} error={errors.name} data-testid="contact-name" />
                      </Field>
                      <Field label="Company"><TextInput value={form.company} onChange={set("company")} data-testid="contact-company" /></Field>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-6">
                      <Field label="Email" required error={errors.email}>
                        <TextInput type="email" value={form.email} onChange={set("email")} error={errors.email} data-testid="contact-email-input" />
                      </Field>
                      <Field label="Phone"><TextInput value={form.phone} onChange={set("phone")} data-testid="contact-phone-input" /></Field>
                    </div>
                    <Field label="Reason for Contact" required error={errors.reason}>
                      <NativeSelect value={form.reason} onChange={set("reason")} error={errors.reason} data-testid="contact-reason">
                        <option value="">Select a reason…</option>
                        {reasons.map((r) => <option key={r} value={r}>{r}</option>)}
                      </NativeSelect>
                    </Field>
                    <Field label="Message" required error={errors.message}>
                      <TextArea rows={5} value={form.message} onChange={set("message")} error={errors.message} data-testid="contact-message" />
                    </Field>
                    <AntiSpam onChange={setSpam} testid="contact-antispam" />
                    <button type="submit" disabled={submitting} className={`${btn.gold} w-full disabled:opacity-60`} data-testid="contact-submit">
                      {submitting ? "Sending…" : "Contact Redwood Capital"} {!submitting && <ArrowRight size={18} />}
                    </button>
                  </form>
                )}
              </div>
            </Reveal>
          </div>
        </Container>
      </section>
    </div>
  );
}
